import { m, useTransform } from 'framer-motion';
// @mui
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';
//
import { WS } from '../../../theme/stage';
import { ArtProps } from './TondTondArt';

// ----------------------------------------------------------------------

const FILL = { position: 'absolute', inset: 0, width: '100%', height: '100%' } as const;

const FIELD = `radial-gradient(120% 90% at 50% 35%, ${WS.ocean} 0%, ${WS.oceanDeep} 78%)`;

const flow = keyframes`
  to { stroke-dashoffset: -28; }
`;

const ripple = keyframes`
  from { transform: scale(0.4); opacity: 0.9; }
  to { transform: scale(2.6); opacity: 0; }
`;

const LANDS = [
  { d: 'M40 120C44 92 70 74 98 80C122 86 128 110 118 132C110 150 118 176 104 200C92 220 70 214 64 190C58 166 38 150 40 120Z', fill: WS.landSage },
  { d: 'M160 98C170 76 206 70 226 84C240 94 232 116 244 132C256 150 246 184 224 200C206 214 186 204 182 182C178 160 150 122 160 98Z', fill: WS.land },
  { d: 'M262 88C284 70 332 72 352 96C368 116 356 140 334 150C316 158 300 176 280 170C262 164 268 142 258 126C250 112 250 98 262 88Z', fill: WS.landLilac },
];

type PawnProps = { x: number; y: number; body: string };

function Pawn({ x, y, body }: PawnProps) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse cx="0" cy="2" rx="15" ry="4.5" fill="rgba(3,9,26,.45)" />
      <ellipse cx="0" cy="0" rx="13" ry="3.8" fill="#F6EEDC" stroke={WS.ink} strokeWidth="1.6" />
      <path d="M-9 -2C-9 -14 -6 -19 0 -19C6 -19 9 -14 9 -2Z" fill={body} stroke={WS.ink} strokeWidth="1.6" />
      <circle cx="0" cy="-27" r="9" fill="#F0CDB3" stroke={WS.ink} strokeWidth="1.6" />
    </g>
  );
}

export default function WorldstageArt({ fan, animated }: ArtProps) {
  const lift = useTransform(fan, (v) => -6 * v);
  const scale = useTransform(fan, (v) => 0.94 + 0.08 * v);

  const play = (animation: string) => ({
    '@media (hover: hover) and (prefers-reduced-motion: no-preference)': {
      'article:hover &': { animation },
    },
  });

  return (
    <>
      <Box sx={{ ...FILL, backgroundImage: FIELD }} />

      <Box component="svg" viewBox="0 0 400 300" sx={FILL}>
        <g fill="none" stroke="rgba(170,196,235,.12)" strokeWidth="1">
          <ellipse cx="200" cy="150" rx="190" ry="118" vectorEffect="non-scaling-stroke" />
          <ellipse cx="200" cy="150" rx="120" ry="118" vectorEffect="non-scaling-stroke" />
          <ellipse cx="200" cy="150" rx="46" ry="118" vectorEffect="non-scaling-stroke" />
          <path d="M14 110H386M14 190H386" vectorEffect="non-scaling-stroke" />
        </g>

        {LANDS.map((land) => (
          <path key={land.d} d={land.d} fill={land.fill} stroke="#FFFAF0" strokeWidth="1.5" />
        ))}

        <Box
          component="path"
          d="M88 150Q170 70 300 124"
          fill="none"
          stroke={WS.agreement}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="9 5"
          sx={play(`${flow} 1.6s linear infinite`)}
        />

        <g transform="translate(212 150)">
          <Box
            component="circle"
            r="10"
            fill="none"
            stroke={WS.conflict}
            strokeWidth="2.5"
            sx={{ transformBox: 'fill-box', transformOrigin: 'center', ...play(`${ripple} 2.2s ease-out infinite`) }}
          />
          <circle r="5.5" fill={WS.conflict} stroke={WS.ink} strokeWidth="1.4" />
        </g>

        <Pawn x={88} y={176} body={WS.gold} />
        <Pawn x={300} y={150} body="#8D87A6" />
      </Box>

      <m.div
        style={{
          position: 'absolute',
          left: '8%',
          top: '12%',
          width: '34%',
          transformOrigin: '20% 100%',
          willChange: animated ? 'transform' : undefined,
          y: lift,
          scale,
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 136 64"
          sx={{ display: 'block', width: '100%', filter: 'drop-shadow(0 4px 8px rgba(3,9,26,.45))' }}
        >
          <path
            d="M10 2H126A8 8 0 0 1 134 10V40A8 8 0 0 1 126 48H38L24 62L26 48H10A8 8 0 0 1 2 40V10A8 8 0 0 1 10 2Z"
            fill="#FFFDF8"
            stroke={WS.ink}
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <rect x="14" y="14" width="40" height="6" rx="3" fill={WS.gold} />
          <rect x="14" y="27" width="104" height="6" rx="3" fill="#CFC6B4" />
        </Box>
      </m.div>
    </>
  );
}
